import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ViewSwitchHint } from "@/components/ViewSwitchHint";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";

import Landing from "@/pages/marketing/Landing";
import Framework from "@/pages/marketing/Framework";
import HowItWorks from "@/pages/marketing/HowItWorks";
import Audiences from "@/pages/marketing/Audiences";
import Research from "@/pages/marketing/Research";
import FAQ from "@/pages/marketing/FAQ";
import Privacy from "@/pages/marketing/Privacy";
import Supporters from "@/pages/marketing/Supporters";
import { ForStudents, ForEmployers, ForEducators, ForAdministrators } from "@/pages/marketing/AudienceSplashes";
import Access from "@/pages/Access";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SignUp from "@/pages/SignUp";
import VerifyEmail from "@/pages/VerifyEmail";
import RegistrationRespond from "@/pages/RegistrationRespond";

import StudentHome from "@/pages/student/StudentHome";
import WhatIfPage from "@/pages/student/WhatIfPage";
import EmploymentProfile from "@/pages/student/EmploymentProfile";
import LearningAcademy from "@/pages/student/LearningAcademy";
import CoachPage from "@/pages/student/CoachPage";
import InterestProfile from "@/pages/student/InterestProfile";
import AdvisorHome, { AdvisorStudent } from "@/pages/advisor/AdvisorHome";
import AdvisorSkillsUpdate from "@/pages/advisor/AdvisorSkillsUpdate";
import AdvisorUpdates from "@/pages/student/AdvisorUpdates";
import AdminHome, { AdminAudit, AdminSupporters } from "@/pages/admin/AdminHome";
import { AdminAssessments } from "@/pages/admin/AdminAssessments";
import { AdminOccupations } from "@/pages/admin/AdminOccupations";
import { AdminUsers } from "@/pages/admin/AdminUsers";
import { AdminEmailTemplates } from "@/pages/admin/AdminEmailTemplates";
import { AdminRegistrations } from "@/pages/admin/AdminRegistrations";
import { AdminTasks } from "@/pages/admin/AdminTasks";
import { AdminSuggestions } from "@/pages/admin/AdminSuggestions";
import MyTasks from "@/pages/MyTasks";
import SubmitSuggestion from "@/pages/SubmitSuggestion";
import MyProfile from "@/pages/MyProfile";
import HelpCenter from "@/pages/HelpCenter";
import { AdminCertifications } from "@/pages/admin/AdminCertifications";
import EmployerHome from "@/pages/employer/EmployerHome";
import EmployerJobs from "@/pages/employer/EmployerJobs";
import StudentJobs from "@/pages/student/StudentJobs";
import DataSources from "@/pages/marketing/DataSources";
import CareerExplorer from "@/pages/marketing/CareerExplorer";
import AdvisorInterestView from "@/pages/advisor/AdvisorInterestView";
import AdvisorAcademy from "@/pages/advisor/AdvisorAcademy";
import AdvisorCertification from "@/pages/advisor/AdvisorCertification";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Capture a ?ref=CODE referral param on any entry point (before the auth gate).
function RefCapture() {
  const { search } = useLocation();
  useEffect(() => {
    const code = new URLSearchParams(search).get("ref");
    if (code) localStorage.setItem("gpaplus_ref", code);
  }, [search]);
  return null;
}

// Site-wide gate: nothing is visible until the visitor is identified (team-only preview).
function RequireAuth() {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null)
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#003057]" />
      </div>
    );
  if (!user) return <Navigate to="/access" replace state={{ from: location }} />;
  return <Outlet />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <RefCapture />
          <Routes>
            {/* Anonymous-allowed */}
            <Route path="/access" element={<Access />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/registration/respond" element={<RegistrationRespond />} />

            {/* Everything else requires an identified user */}
            <Route element={<RequireAuth />}>
              {/* Marketing */}
              <Route path="/" element={<Landing />} />
              <Route path="/framework" element={<Framework />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/audiences" element={<Audiences />} />
              <Route path="/research" element={<Research />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/supporters" element={<Supporters />} />
              <Route path="/data-sources" element={<DataSources />} />
              <Route path="/career-explorer" element={<CareerExplorer />} />
              <Route path="/for-students" element={<ForStudents />} />
              <Route path="/for-employers" element={<ForEmployers />} />
              <Route path="/for-educators" element={<ForEducators />} />
              <Route path="/for-administrators" element={<ForAdministrators />} />

              {/* Student */}
              <Route path="/app/student" element={<ProtectedRoute roles={["student"]}><StudentHome /></ProtectedRoute>} />
              <Route path="/app/student/whatif" element={<ProtectedRoute roles={["student"]}><WhatIfPage /></ProtectedRoute>} />
              <Route path="/app/student/employment-profile" element={<ProtectedRoute roles={["student"]}><EmploymentProfile /></ProtectedRoute>} />
              <Route path="/app/student/academy" element={<ProtectedRoute roles={["student"]}><LearningAcademy /></ProtectedRoute>} />
              <Route path="/app/student/coach" element={<ProtectedRoute roles={["student"]}><CoachPage /></ProtectedRoute>} />
              <Route path="/app/student/interests" element={<ProtectedRoute roles={["student"]}><InterestProfile /></ProtectedRoute>} />
              <Route path="/app/student/advisor-updates" element={<ProtectedRoute roles={["student"]}><AdvisorUpdates /></ProtectedRoute>} />

              {/* Advisor */}
              <Route path="/app/advisor" element={<ProtectedRoute roles={["advisor", "faculty", "admin"]}><AdvisorHome /></ProtectedRoute>} />
              <Route path="/app/advisor/skills-update" element={<ProtectedRoute roles={["advisor", "faculty", "admin"]}><AdvisorSkillsUpdate /></ProtectedRoute>} />
              <Route path="/app/advisor/interest/:token" element={<ProtectedRoute roles={["advisor", "faculty", "admin"]}><AdvisorInterestView /></ProtectedRoute>} />
              <Route path="/app/advisor/academy" element={<ProtectedRoute roles={["advisor", "faculty", "admin"]}><AdvisorAcademy /></ProtectedRoute>} />
              <Route path="/app/advisor/certification" element={<ProtectedRoute roles={["advisor", "faculty", "admin"]}><AdvisorCertification /></ProtectedRoute>} />
              <Route path="/app/advisor/:id" element={<ProtectedRoute roles={["advisor", "faculty", "admin"]}><AdvisorStudent /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/app/admin" element={<ProtectedRoute roles={["admin"]}><AdminHome /></ProtectedRoute>} />
              <Route path="/app/admin/audit" element={<ProtectedRoute roles={["admin"]}><AdminAudit /></ProtectedRoute>} />
              <Route path="/app/admin/assessments" element={<ProtectedRoute roles={["admin"]}><AdminAssessments /></ProtectedRoute>} />
              <Route path="/app/admin/occupations" element={<ProtectedRoute roles={["admin"]}><AdminOccupations /></ProtectedRoute>} />
              <Route path="/app/admin/certifications" element={<ProtectedRoute roles={["admin"]}><AdminCertifications /></ProtectedRoute>} />
              <Route path="/app/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
              <Route path="/app/admin/communications" element={<ProtectedRoute roles={["admin"]}><AdminEmailTemplates /></ProtectedRoute>} />
              <Route path="/app/admin/registrations" element={<ProtectedRoute roles={["admin"]}><AdminRegistrations /></ProtectedRoute>} />
              <Route path="/app/admin/tasks" element={<ProtectedRoute roles={["admin"]}><AdminTasks /></ProtectedRoute>} />
              <Route path="/app/admin/suggestions" element={<ProtectedRoute roles={["admin"]}><AdminSuggestions /></ProtectedRoute>} />
              <Route path="/app/tasks" element={<ProtectedRoute roles={["student", "faculty", "advisor", "employer", "admin"]}><MyTasks /></ProtectedRoute>} />
              <Route path="/app/suggestions" element={<ProtectedRoute roles={["student", "faculty", "advisor", "employer", "admin"]}><SubmitSuggestion /></ProtectedRoute>} />
              <Route path="/app/profile" element={<ProtectedRoute roles={["student", "faculty", "advisor", "employer", "admin"]}><MyProfile /></ProtectedRoute>} />
              <Route path="/app/help" element={<ProtectedRoute roles={["student", "faculty", "advisor", "employer", "admin"]}><HelpCenter /></ProtectedRoute>} />
              <Route path="/app/admin/supporters" element={<ProtectedRoute roles={["admin"]}><AdminSupporters /></ProtectedRoute>} />

              {/* Employer */}
              <Route path="/app/employer" element={<ProtectedRoute roles={["employer"]}><EmployerHome /></ProtectedRoute>} />
              <Route path="/app/employer/jobs" element={<ProtectedRoute roles={["employer", "admin"]}><EmployerJobs /></ProtectedRoute>} />
              <Route path="/app/student/jobs" element={<ProtectedRoute roles={["student"]}><StudentJobs /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          <ViewSwitchHint />
          <ForcePasswordChange />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
